import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { useSnapshot } from 'valtio/react';

import { Carousel } from '@paid-ui/components/carousel';
import { Logo } from '@paid-ui/components/logo';
import { SegmentedControlItem, SegmentedControlRoot } from '@paid-ui/components/segmented-control';
import { Separator } from '@paid-ui/components/separator';
import { signUpManager } from '@paid-ui/forms/sign-up/models';
import { SignUpStep } from '@paid-ui/forms/sign-up/models/enums';
import { SuccessIcon } from '@paid-ui/icons/success';

type WrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  src: string;
  alt?: string;
  text?: string;
};

const LogoWrapper: React.FC<WrapperProps> = ({ src, alt, className }) => {
  return (
    <div className="rounded-md bg-white p-[30px] shadow-md">
      <img src={src} alt={alt} className={className} />
    </div>
  );
};

const SmartWrapper: React.FC<WrapperProps> = ({ src, alt, className, text }) => {
  return (
    <div className="grid w-auto items-center justify-center gap-[18px]">
      <img src={src} alt={alt} className={className} />
      <footer className="flex items-center justify-center gap-2">
        <SuccessIcon className="h-[25px] w-[25px]" />
        <div className="text-xs font-medium">{text}</div>
      </footer>
    </div>
  );
};

const SlideOne = () => (
  <div className="grid h-[130px] w-full grid-cols-3 gap-4 rounded-[20px] border border-grey-hover bg-grey-lightest p-5">
    <LogoWrapper src="/sign/mba.png" alt="MBA" className="h-[30px] w-[120px]" />
    <LogoWrapper src="/sign/aia.png" alt="AIA" className="h-[30px] w-[120px]" />
    <LogoWrapper src="/sign/rbn.png" alt="RBN" className="h-[30px] w-[120px]" />
  </div>
);

const SlideTwo = () => (
  <div className="grid w-full grid-cols-3 gap-3">
    <SmartWrapper
      src="/sign/esignature.png"
      alt="digital"
      className="h-[120px]"
      text="Digital contracts."
    />
    <SmartWrapper
      src="/sign/deposit.png"
      alt="digital"
      className="h-[120px]"
      text="Fast, secure payments."
    />
    <SmartWrapper
      src="/sign/digital.png"
      alt="digital"
      className="h-[120px]"
      text="In-app digital claims."
    />
  </div>
);
const CarouselForLargeScreen = () => {
  return (
    <div className="hidden h-full w-full items-center justify-center border border-y-0 border-l-0 border-grey-hover px-[100px] py-12 xl:grid">
      <div className="grid justify-center gap-[49px] xl:w-[550px]">
        <div className="text-[48px] font-extrabold leading-[54px]">
          <div>Home of the world’s</div>
          <div>
            <span className="text-blue"> smartest </span>
            <span>building</span>
          </div>
          <span>contracts.</span>
        </div>
        <Carousel slides={[<SlideOne key={0} />, <SlideTwo key={1} />]} autoPlay loop />
      </div>
    </div>
  );
};

const CarouselForSmallScreen = () => (
  <section className="top-0 z-10 grid w-full gap-[30px] bg-white pt-[30px] md:absolute xl:hidden">
    <div className="px-[40px] text-center text-xl font-bold">
      <span>Home of the world’s</span>
      <span className="text-blue"> smartest </span>
      <span>building contracts.</span>
    </div>
    <Separator className="h-[1px] w-full bg-grey-hover" />
    <div className="flex items-center justify-center gap-[33px] border-b border-grey-hover px-[35px] pb-[29px]">
      <img src="/sign/aia.png" alt="MBA" className="h-[23px] w-[90px]" />
      <img src="/sign/mba.png" alt="MBA" className="h-[24px] w-[97px]" />
      <img src="/sign/rbn.png" alt="MBA" className="h-[17px] w-[64px]" />
    </div>
  </section>
);

const SignFooter = () => (
  <div className="flex items-center justify-center gap-3 bg-grey-lightest">
    <span className="text-sm font-medium text-grey-tertiary">Powered by</span>
    <Logo className="h-[20px] w-[70px]" />
  </div>
);

interface SignContainerProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

export const SignContainer: React.FC<SignContainerProps> = ({ title, description, children }) => {
  const router = useRouter();
  const pathname = usePathname();
  const { currentStep } = useSnapshot(signUpManager);

  const handlePageChange = (value: string) => {
    router.push(`${value}/${globalThis.location.search}`);
  };

  return (
    <div className="relative h-screen w-screen bg-white">
      <NextSeo title={title} description={description} />
      <div className="flex h-full w-full flex-col items-center justify-center overflow-y-hidden">
        <div className="relative grid h-full w-full grid-cols-1 items-center justify-center overflow-y-auto bg-grey-lightest lg:bg-white xl:grid-cols-[3fr_2fr] xl:pl-[100px]">
          <CarouselForLargeScreen />
          <CarouselForSmallScreen />
          <div className="relative flex h-full w-full flex-col items-center justify-center gap-[40px] bg-grey-lightest p-5">
            <div className="flex h-auto w-full max-w-[356px] flex-col gap-9 overflow-y-auto rounded-2xl rounded-tl-none bg-white p-10 px-6 shadow-lg">
              {pathname === '/signup' && currentStep !== SignUpStep.TYPE_AND_EMAIL ? null : (
                <div>
                  <SegmentedControlRoot value={pathname} onValueChange={handlePageChange}>
                    <SegmentedControlItem value="/signin">Sign in</SegmentedControlItem>
                    <SegmentedControlItem value="/signup">Sign up</SegmentedControlItem>
                  </SegmentedControlRoot>
                </div>
              )}
              {children}
            </div>
            <SignFooter />
          </div>
        </div>
      </div>
    </div>
  );
};
